import { useEffect, useRef, useState } from "react";
import { Device } from "@twilio/voice-sdk";
import axios from "axios";
import { useAuth } from "@/contexts/TradeAuthContext";
import { PhoneCall, Mic, X } from "lucide-react";
import { toast } from "sonner";

const normalizePhone = (value) => {
  const cleaned = (value || "").trim().replace(/[\s-]/g, "");
  return cleaned && !cleaned.startsWith("+") ? `+${cleaned}` : cleaned;
};

const fmt = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

// Real browser call through Twilio Voice; Twilio records the call and callback saves it to Documents.
const RecordedCallButton = ({ phone, name, contactId = "", contactType = "contact", className = "", testid }) => {
  const { API, authHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [message, setMessage] = useState("");
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (callRef.current) callRef.current.disconnect();
    if (deviceRef.current) deviceRef.current.destroy();
  }, []);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const cleanupDevice = () => {
    stopTimer();
    if (callRef.current) {
      callRef.current.removeAllListeners();
      callRef.current = null;
    }
    if (deviceRef.current) {
      deviceRef.current.removeAllListeners();
      deviceRef.current.destroy();
      deviceRef.current = null;
    }
  };

  const startRecordedCall = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
    setStatus("connecting");
    setSeconds(0);
    setMessage("Preparing recorded call...");
    try {
      const statusRes = await axios.get(`${API}/trade/voice/status`, { headers: authHeader() });
      if (!statusRes.data.configured) throw new Error("Twilio Voice is not fully configured");
      const tokenRes = await axios.get(`${API}/trade/voice/token`, { headers: authHeader() });
      const device = new Device(tokenRes.data.token, { codecPreferences: ["opus", "pcmu"], enableIceRestart: true });
      deviceRef.current = device;

      device.on("error", (error) => {
        setStatus("failed");
        setMessage(error.message || "Call failed");
        toast.error(error.message || "Call failed");
      });

      await device.register();
      setMessage("Calling...");
      const call = await device.connect({
        params: {
          To: normalizePhone(phone),
          contact_id: contactId,
          contact_name: name,
          contact_type: contactType
        }
      });
      callRef.current = call;

      call.on("accept", () => {
        setStatus("in-call");
        setMessage("Call connected — recording is on");
        startTimer();
      });
      call.on("disconnect", () => {
        setStatus("ended");
        setMessage("Call ended. Recording will save to Documents automatically.");
        stopTimer();
      });
      call.on("cancel", () => {
        setStatus("ended");
        setMessage("Call cancelled");
        stopTimer();
      });
      call.on("error", (error) => {
        setStatus("failed");
        setMessage(error.message || "Call failed");
        stopTimer();
      });
    } catch (error) {
      setStatus("failed");
      setMessage(error.response?.data?.detail || error.message || "Could not start recorded call");
      toast.error(error.response?.data?.detail || error.message || "Could not start recorded call");
    }
  };

  const hangUp = () => {
    if (callRef.current) callRef.current.disconnect();
    if (deviceRef.current) deviceRef.current.disconnectAll();
    setStatus("ended");
    setMessage("Call ended. Recording will save to Documents automatically.");
  };

  const close = () => {
    cleanupDevice();
    setOpen(false);
    setStatus("idle");
    setSeconds(0);
    setMessage("");
  };

  return (
    <>
      <button
        type="button"
        onClick={startRecordedCall}
        className={className}
        data-testid={testid}
      >
        <Mic size={12} /> Record Call
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" data-testid="recorded-call-modal">
          <div className="trade-card rounded-sm p-6 w-full max-w-sm text-center" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif font-semibold text-lg">Recorded Call</h3>
              <button onClick={close} className="p-2 text-white/40 hover:text-white" data-testid="recorded-call-close">
                <X size={18} />
              </button>
            </div>
            <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
              <PhoneCall className={`w-7 h-7 trade-gold ${status === "connecting" ? "animate-pulse" : ""}`} />
            </div>
            <p className="font-semibold text-lg" data-testid="recorded-call-contact">{name}</p>
            <p className="text-sm text-white/45 mb-2">{phone}</p>
            <p className="text-sm trade-gold mb-1" data-testid="recorded-call-status">{message}</p>
            {status === "in-call" && <p className="text-2xl font-serif text-white mb-3" data-testid="recorded-call-timer">{fmt(seconds)}</p>}
            <p className="text-[11px] text-white/35 mb-5">The call announces recording consent. Completed recordings are saved in Documents → Miscellaneous.</p>
            {status === "in-call" || status === "connecting" ? (
              <button onClick={hangUp} className="w-full bg-red-500 text-white hover:bg-red-600 rounded-sm h-11 text-sm font-semibold" data-testid="recorded-call-hangup">
                Hang Up
              </button>
            ) : (
              <button onClick={close} className="w-full bg-white/10 text-white hover:bg-white/20 rounded-sm h-11 text-sm" data-testid="recorded-call-done">
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RecordedCallButton;
